import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ToolStats = {
  totalTools: number;
  totalValueUsd: number;
  totalValueLabel: string;
  featuredCount: number;
  bookToolsCount: number;
  loading: boolean;
  error: string | null;
};

const DEFAULT: ToolStats = {
  totalTools: 0,
  totalValueUsd: 0,
  totalValueLabel: "$0",
  featuredCount: 0,
  bookToolsCount: 0,
  loading: true,
  error: null,
};

// Parse credit_value text (e.g. "$25,000 in credits", "Up to $100,000 in AWS credits",
// "$50,000 value (1 year free)") into a USD number. Returns 0 if no $ amount found.
export function parseCreditValueUsd(value: string | null | undefined): number {
  if (!value) return 0;
  const matches = value.match(/\$\s?([\d,]+(?:\.\d+)?)/g);
  if (!matches || matches.length === 0) return 0;
  // Take the largest dollar figure mentioned (handles "$9/mo" vs "$50,000 value").
  let max = 0;
  for (const m of matches) {
    const n = Number(m.replace(/[^\d.]/g, ""));
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return max;
}

export function formatCreditTotal(usd: number): string {
  if (usd >= 1_000_000) {
    const m = usd / 1_000_000;
    const rounded = m >= 10 ? Math.floor(m) : Math.round(m * 10) / 10;
    return `$${rounded}M+`;
  }
  if (usd >= 1_000) {
    return `$${Math.round(usd / 1_000)}K+`;
  }
  return `$${usd}`;
}

let cache: { promise: Promise<Omit<ToolStats, "loading">> | null } = { promise: null };

async function fetchStats(): Promise<Omit<ToolStats, "loading">> {
  const { data, error } = await supabase
    .from("tools")
    .select("credit_value, is_featured, book_tip, is_active")
    .eq("is_active", true);

  if (error) {
    return {
      totalTools: 0,
      totalValueUsd: 0,
      totalValueLabel: "$0",
      featuredCount: 0,
      bookToolsCount: 0,
      error: error.message,
    };
  }

  const rows = data ?? [];
  let totalValueUsd = 0;
  let featuredCount = 0;
  let bookToolsCount = 0;
  for (const r of rows as Array<{ credit_value: string | null; is_featured: boolean | null; book_tip: boolean | null }>) {
    totalValueUsd += parseCreditValueUsd(r.credit_value);
    if (r.is_featured) featuredCount += 1;
    if (r.book_tip) bookToolsCount += 1;
  }

  return {
    totalTools: rows.length,
    totalValueUsd,
    totalValueLabel: formatCreditTotal(totalValueUsd),
    featuredCount,
    bookToolsCount,
    error: null,
  };
}

export function useToolStats(): ToolStats {
  const [stats, setStats] = useState<ToolStats>(DEFAULT);

  useEffect(() => {
    let cancelled = false;
    if (!cache.promise) cache.promise = fetchStats();
    cache.promise.then((s) => {
      if (!cancelled) setStats({ ...s, loading: false });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return stats;
}
